import React, { type JSX } from 'react';
import { I18nextProvider } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import type { AppProps } from 'next/app';
import Head from 'next/head';

import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { SpeedInsights } from '@vercel/speed-insights/next';

import { queryClient } from '@/utils/api/queryClient';
import i18n from '@/utils/i18n/i18n';
import EppoRandomizationProvider from '@/utils/providers/EppoRandomizationProvider';
import { MantineProvider } from '@/utils/providers/MantineProvider';
import NavigationProvider from '@/utils/providers/NavigationProvider';

import '../../styles/globals.css';

interface PageProps {
  NavigationWrapper?: (page: JSX.Element) => JSX.Element;
}

function MyApp({ Component, pageProps }: AppProps) {
  const getLayout = (Component as PageProps)?.NavigationWrapper || ((page) => page);

  return (
    <>
      <Head>
        <title>YoungOnes Admin Panel</title>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>
      <EppoRandomizationProvider>
        <MantineProvider>
          <QueryClientProvider client={queryClient}>
            <I18nextProvider i18n={i18n}>
              <ToastContainer
                bodyClassName="gap-8 p-0 pr-1"
                position="top-center"
                autoClose={5000}
              />
              <NavigationProvider>
                {getLayout(<Component {...pageProps}></Component>)}
                <SpeedInsights />
              </NavigationProvider>
              <ReactQueryDevtools initialIsOpen={false} position="right" />
            </I18nextProvider>
          </QueryClientProvider>
        </MantineProvider>
      </EppoRandomizationProvider>
    </>
  );
}

export default MyApp;
